import React, { useEffect, useState } from 'react';

import { Pagination, useDevice } from '@tecma/ds';
import { useTranslation } from '@tecma/i18n';

import { useSwiperController } from 'hooks/useSwiperController';

import CardQuote from 'components/CardQuote/CardQuote';
import type { CardQuoteProps } from 'components/CardQuote/CardQuote';
import { SwiperCarousel } from 'components/SwiperCarousel';

import './card-quote-list.scss';

interface CardQuoteListRequiredProps {
  quotes: CardQuoteProps[];
}

// Optional Props
interface CardQuoteListOptionalProps {
  selectedQuoteId?: string;
}

// Combined required and optional props to build the full prop interface
export interface CardQuoteListProps extends CardQuoteListRequiredProps, CardQuoteListOptionalProps {}

const groupCards = (items: React.ReactNode[], n: number) => {
  const grouped: React.ReactNode[] = [];
  for (let i = 0; i < items.length; i += n) {
    grouped.push(items.slice(i, i + n));
  }
  return grouped;
};

const CardQuoteList: React.FC<CardQuoteListProps> = ({ quotes, selectedQuoteId }) => {
  const { t } = useTranslation();
  const device = useDevice();
  const [selectedItemIndex, setSelectedItemIndex] = useState(0);
  const [slidesPerView, setSlidesPerView] = useState(1);
  const pages = Math.ceil(quotes.length / slidesPerView);
  const swiperController = useSwiperController();

  const next = () => {
    swiperController.handleNextClick();
    setSelectedItemIndex(swiperController.activeIndex + 1);
  };
  const prev = () => {
    swiperController.handlePrevClick();
    setSelectedItemIndex(swiperController.activeIndex - 1);
  };
  const handleSlideTo = (page: number) => {
    swiperController.handleSlideTo(page - 1);
    setSelectedItemIndex(page - 1);
  };

  const shouldUsePagination = () => {
    if (device.typeDetailed === 'desktop_s' && quotes.length > 2) {
      return true;
    }
    return device.type === 'desktop' && quotes.length > 3;
  };

  const getSelectedQuotePage = (quoteId: string) => {
    const cardIndex = quotes.findIndex((quote) => quote.id === quoteId) + 1;
    const cardPage = Math.ceil(cardIndex / slidesPerView);
    swiperController.handleSlideTo(cardPage - 1);
    setSelectedItemIndex(cardPage - 1);
  };

  const checkQuoteListMaxHeight = () => {
    const container = document.querySelector('.quote-list-carousel');
    if (container?.clientHeight) {
      setSlidesPerView(Math.floor(container.clientHeight / 198) || 1);
    }
  };

  useEffect(() => {
    if (shouldUsePagination()) {
      getSelectedQuotePage(selectedQuoteId!);
    }
  }, [slidesPerView]);

  useEffect(() => {
    if (shouldUsePagination()) {
      checkQuoteListMaxHeight();
      getSelectedQuotePage(selectedQuoteId!);
    }
    window.addEventListener('resize', checkQuoteListMaxHeight);
    return () => {
      window.removeEventListener('resize', checkQuoteListMaxHeight);
    };
  }, []);

  return (
    <div className='card-quote-list'>
      <span className='quotes-counter'>
        {quotes.length} {t('details-page.configurations', { count: quotes.length })}
      </span>
      {shouldUsePagination() ? (
        <>
          <SwiperCarousel
            items={groupCards(
              quotes.map((quote) => <CardQuote isSelected={quote.id === selectedQuoteId} key={quote.id} {...quote} />),
              slidesPerView,
            )}
            className='quote-list-carousel'
            swiperProps={{ direction: 'vertical', speed: 0 }}
            swiperController={swiperController}
            hideArrowButtons
          />
          <Pagination
            pages={pages}
            currentPage={selectedItemIndex + 1}
            setCurrentPage={handleSlideTo}
            setNextPage={next}
            setPrevPage={prev}
            pagesToShowCloseTheCurrent={pages <= 2 ? 0 : 1}
            outlined
            nextButtonIcon='chevron-right'
            prevButtonIcon='chevron-left'
          />
        </>
      ) : (
        quotes.map((quote) => <CardQuote isSelected={quote.id === selectedQuoteId} key={quote.id} {...quote} />)
      )}
    </div>
  );
};

export default React.memo(CardQuoteList);
